// FILE: src/pages/Clients/ClientFormModal.jsx
import React, { useState, useEffect } from 'react';
import { clientsAPI } from '../../api/services';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';

// ── Empty form template ───────────────────────────────────
const EMPTY = {
  FirstName:'', MiddleName:'', LastName:'', DateOfBirth:'', Gender:'',
  Ethnicity:'', PhoneNumber:'', CellNumber:'', Email:'', Address:'',
  City:'', State:'', ZipCode:'', MedicaidID:'', MedicareID:'',
  InsuranceProvider:'', PolicyNumber:'', InsuranceRenewalDate:'',
  PrimaryContact:'', EmergencyContact:'', EmergencyContactRelationship:'',
  EmergencyContactPhone:'', RequestedStartDate:'', ServiceType:'',
  PreferredCaregiverGender:'', Coordinator:'', Language:'',
};

const TABS = ['Personal', 'Contact', 'Insurance', 'Service'];

// ── Field component — MUST be defined OUTSIDE the parent
//    component so React never unmounts/remounts it on re-render
//    (defining it inside causes focus loss on every keystroke)
function Field({ label, field, type, required, options, span, form, errors, onChange }) {
  const hasError = errors && errors[field];
  return (
    <div className="form-group" style={span ? { gridColumn: `span ${span}` } : {}}>
      <label className="form-label">
        {label}{required && <span className="req"> *</span>}
      </label>
      {options ? (
        <select
          className="form-control"
          value={form[field] ?? ''}
          onChange={e => onChange(field, e.target.value)}
        >
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          className={`form-control${hasError ? ' input-error' : ''}`}
          type={type || 'text'}
          value={form[field] ?? ''}
          onChange={e => onChange(field, e.target.value)}
          placeholder={label}
        />
      )}
      {hasError && (
        <div style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 3 }}>
          {errors[field]}
        </div>
      )}
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────
export default function ClientFormModal({ client, onClose, onSaved }) {
  const isEdit = !!client;
  const [tab,    setTab]    = useState(0);
  const [form,   setForm]   = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-fill form when editing
  useEffect(() => {
    if (client) {
      const filled = {};
      Object.keys(EMPTY).forEach(k => {
        if (['DateOfBirth', 'InsuranceRenewalDate', 'RequestedStartDate'].includes(k)) {
          filled[k] = client[k] ? client[k].split('T')[0] : '';
        } else {
          filled[k] = client[k] ?? '';
        }
      });
      setForm(filled);
    }
  }, [client]);

  // Stable onChange — does NOT recreate Field component
  const handleChange = (field, val) => {
    setForm(p => ({ ...p, [field]: val }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.FirstName.trim()) e.FirstName = 'Required';
    if (!form.LastName.trim())  e.LastName  = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { setTab(0); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await clientsAPI.update(client.ID, form);
        toast.success('Client updated successfully.');
      } else {
        await clientsAPI.create(form);
        toast.success('Client added successfully.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Shared props passed to every Field — stable references
  const fieldProps = { form, errors, onChange: handleChange };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">
            {isEdit ? `✏️ Edit Client — ${client.FirstName} ${client.LastName}` : '➕ Add New Client'}
          </span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          {TABS.map((t, i) => (
            <button
              key={t}
              className={`modal-tab${tab === i ? ' active' : ''}`}
              onClick={() => setTab(i)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="modal-body">

          {/* Tab 0 — Personal */}
          {tab === 0 && (
            <div className="form-row-3">
              <Field label="First Name"  field="FirstName"  required {...fieldProps} />
              <Field label="Middle Name" field="MiddleName" {...fieldProps} />
              <Field label="Last Name"   field="LastName"   required {...fieldProps} />
              <Field label="Date of Birth" field="DateOfBirth" type="date" {...fieldProps} />
              <Field label="Gender" field="Gender"
                options={['Male','Female','Non-binary','Prefer not to say']} {...fieldProps} />
              <Field label="Ethnicity" field="Ethnicity"
                options={['Hispanic','Non-Hispanic White','Non-Hispanic Black','Asian','Other']} {...fieldProps} />
              <Field label="Preferred Language" field="Language"
                options={['English','Spanish','French','Creole','Other']} {...fieldProps} />
            </div>
          )}

          {/* Tab 1 — Contact */}
          {tab === 1 && (
            <div className="form-row-3">
              <Field label="Phone Number"  field="PhoneNumber" type="tel"   {...fieldProps} />
              <Field label="Cell Number"   field="CellNumber"  type="tel"   {...fieldProps} />
              <Field label="Email"         field="Email"       type="email" {...fieldProps} />
              <Field label="Address" field="Address" span={3} {...fieldProps} />
              <Field label="City"    field="City" {...fieldProps} />
              <Field label="State"   field="State"
                options={['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
                  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
                  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']}
                {...fieldProps} />
              <Field label="Zip Code" field="ZipCode" {...fieldProps} />
              <Field label="Primary Contact"         field="PrimaryContact"              span={2} {...fieldProps} />
              <Field label="Emergency Contact"       field="EmergencyContact"             {...fieldProps} />
              <Field label="Emergency Relationship"  field="EmergencyContactRelationship"
                options={['Spouse','Child','Parent','Sibling','Friend','Other']} {...fieldProps} />
              <Field label="Emergency Phone"         field="EmergencyContactPhone" type="tel" {...fieldProps} />
            </div>
          )}

          {/* Tab 2 — Insurance */}
          {tab === 2 && (
            <div className="form-row-3">
              <Field label="Medicaid ID"            field="MedicaidID"           {...fieldProps} />
              <Field label="Medicare ID"            field="MedicareID"           {...fieldProps} />
              <Field label="Insurance Provider"     field="InsuranceProvider"
                options={['Medicaid','Medicare','Blue Cross Blue Shield','Aetna','Cigna','United Health','Private Pay','Other']}
                {...fieldProps} />
              <Field label="Policy Number"          field="PolicyNumber"         {...fieldProps} />
              <Field label="Insurance Renewal Date" field="InsuranceRenewalDate" type="date" {...fieldProps} />
            </div>
          )}

          {/* Tab 3 — Service */}
          {tab === 3 && (
            <div className="form-row-3">
              <Field label="Service Type" field="ServiceType"
                options={['Personal Care','Companion Care','Skilled Nursing','Physical Therapy','Respite Care','Other']}
                {...fieldProps} />
              <Field label="Requested Start Date"      field="RequestedStartDate"      type="date" {...fieldProps} />
              <Field label="Preferred Caregiver Gender" field="PreferredCaregiverGender"
                options={['No Preference','Male','Female']} {...fieldProps} />
              <Field label="Coordinator" field="Coordinator" span={3} {...fieldProps} />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {tab > 0 && (
              <button className="btn btn-secondary" onClick={() => setTab(t => t - 1)}>← Back</button>
            )}
            {tab < TABS.length - 1 && (
              <button className="btn btn-secondary" onClick={() => setTab(t => t + 1)}>Next →</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? '💾 Update Client' : '✅ Add Client'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}